import React, { useState, useRef, useCallback } from 'react';
import { Upload, Download, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet, ArrowRight, RotateCcw } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Step indicators
const STEPS = ['Upload File', 'Preview & Validate', 'Import Result'];

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`flex items-center gap-2 ${i <= step ? 'text-[#1e3a5f]' : 'text-gray-300'}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors
              ${i < step ? 'bg-[#f0a500] border-[#f0a500] text-white' :
                i === step ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-gray-200 text-gray-300'}`}>
              {i < step ? '✓' : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i === step ? 'text-[#1e3a5f]' : ''}`}>{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-[#f0a500]' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function RowStatusBadge({ row }) {
  if (row.errors.length) return (
    <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
      <XCircle size={13} /> {row.errors[0]}
    </span>
  );
  if (row.duplicate) return (
    <span className="inline-flex items-center gap-1 text-xs text-orange-500 font-medium">
      <AlertTriangle size={13} /> {row.warnings[0] || 'Duplicate — will skip'}
    </span>
  );
  if (row.warnings.length) return (
    <span className="inline-flex items-center gap-1 text-xs text-yellow-600 font-medium">
      <AlertTriangle size={13} /> {row.warnings[0]}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
      <CheckCircle size={13} /> Ready
    </span>
  );
}

export default function ClientImport({ onClose, onImported }) {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { rows, total, valid }
  const [result, setResult] = useState(null);     // { imported, skipped, errors, total }
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  // ─── Template download ────────────────────────────────────────────────────
  const downloadTemplate = async () => {
    try {
      const res = await api.get('/clients/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'KUA_Clients_Import_Template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Template download failed');
    }
  };

  // ─── File selection ─────────────────────────────────��─────────────────────
  const handleFile = useCallback(f => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Only .xlsx, .xls, or .csv files are accepted');
      return;
    }
    setFile(f);
  }, []);

  const onDrop = e => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // ─── Upload for preview ──────────────────────────────���────────────────────
  const handlePreview = async () => {
    if (!file) { toast.error('Please select a file first'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/clients/import/preview', fd);
      if (!res.data.rows.length) { toast.error('No data rows found in file'); return; }
      setPreview(res.data);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to parse file');
    } finally { setLoading(false); }
  };

  // ─── Actual import ──────────────���─────────────────────────────────────────
  const handleImport = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/clients/import/', fd);
      setResult(res.data);
      setStep(2);
      if (res.data.imported > 0) onImported();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Import failed');
    } finally { setLoading(false); }
  };

  const reset = () => {
    setStep(0); setFile(null); setPreview(null); setResult(null);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <StepBar step={step} />

      {/* ── STEP 0: Upload ── */}
      {step === 0 && (
        <div className="space-y-5">
          {/* Template download banner */}
          <div className="flex items-center justify-between bg-[#1e3a5f]/5 border border-[#1e3a5f]/20 rounded-xl p-4">
            <div>
              <p className="text-sm font-semibold text-[#1e3a5f]">Start with the template</p>
              <p className="text-xs text-gray-500 mt-0.5">Download, fill in your clients, then upload here</p>
            </div>
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#264873] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              <Download size={15} /> Download Template
            </button>
          </div>

          {/* Drop zone */}
          <div
            onDrop={onDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all
              ${dragOver ? 'border-[#f0a500] bg-[#f0a500]/5' : file ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-[#1e3a5f]/40 hover:bg-gray-50'}`}>
            <input ref={fileRef} type="file" className="hidden" accept=".xlsx,.xls,.csv"
              onChange={e => handleFile(e.target.files[0])} />
            {file ? (
              <div className="space-y-2">
                <FileSpreadsheet size={40} className="mx-auto text-green-500" />
                <p className="font-semibold text-green-700">{file.name}</p>
                <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB — click to change</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload size={36} className="mx-auto text-gray-300" />
                <p className="text-gray-500 font-medium">Drop your file here or click to browse</p>
                <p className="text-xs text-gray-400">Supports .xlsx, .xls, .csv — max 5 MB</p>
              </div>
            )}
          </div>

          {/* Format notes */}
          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
            <p className="font-semibold text-gray-600 mb-1">Column format:</p>
            <p><span className="font-medium text-gray-700">Required:</span> Client Code + Client Name (both mandatory)</p>
            <p><span className="font-medium text-gray-700">Client Code:</span> Mandatory — any format allowed (e.g. KUA-001, ABC-1, TEST999). Must be unique.</p>
            <p><span className="font-medium text-gray-700">PAN:</span> 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)</p>
            <p><span className="font-medium text-gray-700">Entity Type:</span> Individual / HUF / Firm / LLP / Pvt Ltd / Public Ltd / Trust / AOP</p>
            <p><span className="font-medium text-gray-700">Services:</span> comma-separated — IT Returns, TDS, GST, Audit, ROC, ESI, PF, Payroll, Accounts</p>
            <p><span className="font-medium text-gray-700">Status:</span> Active / Inactive / Prospect (defaults to Active)</p>
          </div>

          <div className="flex gap-3">
            <button onClick={handlePreview} disabled={!file || loading}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? 'Parsing...' : <><ArrowRight size={15} /> Preview & Validate</>}
            </button>
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </div>
      )}

      {/* ── STEP 1: Preview ── */}
      {step === 1 && preview && (
        <div className="space-y-4">
          {/* Summary bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-800">{preview.total}</div>
              <div className="text-xs text-gray-500">Total rows</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{preview.valid}</div>
              <div className="text-xs text-gray-500">Will import</div>
            </div>
            <div className={`rounded-lg p-3 text-center ${preview.total - preview.valid > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className={`text-2xl font-bold ${preview.total - preview.valid > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {preview.total - preview.valid}
              </div>
              <div className="text-xs text-gray-500">Skipped / errors</div>
            </div>
          </div>

          {/* Preview table */}
          <div className="overflow-x-auto rounded-lg border border-gray-100 max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold w-8">#</th>
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold">Code</th>
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold">Client Name</th>
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold">PAN</th>
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold">Entity</th>
                  <th className="text-left px-3 py-2 text-gray-500 font-semibold min-w-[180px]">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={i} className={`border-t ${row.errors.length ? 'bg-red-50' : row.duplicate ? 'bg-orange-50' : row.warnings.length ? 'bg-yellow-50' : 'bg-white'}`}>
                    <td className="px-3 py-2 text-gray-400">{row.rowNum}</td>
                    <td className="px-3 py-2 font-mono text-xs text-blue-700">{row.clientCode || <span className="text-gray-300 italic">auto</span>}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">{row.name || <span className="text-gray-300 italic">blank</span>}</td>
                    <td className="px-3 py-2 font-mono">{row.pan || '—'}</td>
                    <td className="px-3 py-2">{row.entityType}</td>
                    <td className="px-3 py-2"><RowStatusBadge row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.valid === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              ⚠️ No valid rows to import. Please fix the errors in your file and re-upload.
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={handleImport} disabled={preview.valid === 0 || loading}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? 'Importing...' : `Import ${preview.valid} Client${preview.valid !== 1 ? 's' : ''}`}
            </button>
            <button onClick={reset} className="btn-secondary flex items-center gap-2 px-4"><RotateCcw size={14} /> Back</button>
            <button onClick={onClose} className="btn-secondary px-4">Cancel</button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Result ── */}
      {step === 2 && result && (
        <div className="space-y-4">
          {/* Big result */}
          <div className={`rounded-2xl p-6 text-center ${result.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
            {result.imported > 0
              ? <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
              : <AlertTriangle size={48} className="mx-auto text-orange-400 mb-3" />}
            <div className="text-3xl font-bold text-gray-800">{result.imported}</div>
            <div className="text-gray-500 mt-1">clients imported successfully</div>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xl font-bold text-gray-700">{result.total}</div>
              <div className="text-xs text-gray-400">Total rows</div>
            </div>
            {result.skipped.length > 0 && (
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="text-xl font-bold text-orange-600">{result.skipped.length}</div>
                <div className="text-xs text-gray-400">Skipped (dup PAN)</div>
              </div>
            )}
            {result.errors.length > 0 && (
              <div className="bg-red-50 rounded-lg p-3">
                <div className="text-xl font-bold text-red-600">{result.errors.length}</div>
                <div className="text-xs text-gray-400">Errors</div>
              </div>
            )}
          </div>

          {/* Skipped detail */}
          {result.skipped.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-orange-600 mb-1">Skipped (PAN already exists):</p>
              <div className="bg-orange-50 rounded-lg p-3 space-y-1 max-h-32 overflow-y-auto">
                {result.skipped.map((s, i) => (
                  <div key={i} className="flex gap-2 text-xs text-orange-700">
                    <span className="font-medium">Row {s.rowNum}:</span>
                    <span>{s.name}</span>
                    {s.pan && <span className="font-mono">({s.pan})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error detail */}
          {result.errors.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-600 mb-1">Errors — not imported:</p>
              <div className="bg-red-50 rounded-lg p-3 space-y-1 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="flex gap-2 text-xs text-red-700">
                    <span className="font-medium">Row {e.rowNum}:</span>
                    <span>{e.name}</span>
                    <span className="text-red-500">— {e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="btn-primary flex-1">Done</button>
            {(result.errors.length > 0 || result.skipped.length > 0) && (
              <button onClick={reset} className="btn-secondary flex items-center gap-2 px-4">
                <RotateCcw size={14} /> Import Another
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
